import type { CSSProperties } from "react";

/**
 * THE PAGE MEASURE SYSTEM (2026-08-11, owner: "why are there such large borders on the left and
 * right ... it puts everything into an extremely narrow field and its terrible looking").
 *
 * WHAT WAS WRONG. `AppShell` took a `width` prop that was a hardcoded pixel number, chosen per
 * SCREEN: intake 880, detail 1000, comps 1240, review 1000, build 880, extension 880, branding 880.
 * Two things follow from that, and both are defects rather than taste:
 *
 *   1. ONE READING MEASURE WAS BEING APPLIED TO TWO KINDS OF CONTENT. 880px is a sensible measure
 *      for a column of prose and form rows. It is the wrong unit entirely for the comparable-sales
 *      grid, which is 18 attribute columns wide and is the screen the agent actually works in.
 *   2. A FIXED PIXEL NUMBER IS WRONG AT EVERY WIDTH EXCEPT ONE. 1240 is simultaneously too wide at
 *      1100 (the page scrolls sideways or the table is crushed) and far too narrow at 1920.
 *      Measured on the live site at a 1512px viewport, step 1 was
 *      `max-width: 880px; margin: 0 276.5px` — 632px, 42% of the glass, empty.
 *
 * THE SYSTEM. A screen declares WHAT KIND OF CONTENT it has. It never picks a pixel count.
 *
 *   prose  880   One column of words and fields, read top to bottom: the address form, the report
 *                narrative, the extension setup, report branding. 880px at 16px Inter is ~95
 *                characters — already at the generous end of the 45–95ch typographic measure, so
 *                widening it would make the text HARDER to read, not easier. A prose page on a
 *                27" monitor is SUPPOSED to have margins.
 *   form  1200   Cards, side-by-side panels and multi-column field rows: the property worksheet,
 *                the review summary, the intake control panel. Derived, not guessed: the review
 *                summary lays its facts out on `repeat(auto-fill, minmax(180px, 1fr))` with an 18px
 *                gap, and six of those modules — the most a reader can scan as one row before it
 *                reads as a wall — is 6×180 + 5×18 = 1170. The worksheet's own field rows are
 *                `22px 1.4fr 1fr 1.2fr`; past ~1200 the value column grows so wide the eye loses
 *                the line back to its label. This is the cap that stops a two-column form from
 *                stretching to 2000px just because the monitor allows it.
 *   data  2044   A table whose width is the sum of its columns. Derived from the thing it exists
 *                for: `ConfirmGrid.COMP_COLUMNS` declares 18 columns whose designed widths total
 *                exactly 2044px. That is the point where the grid has shown everything it has —
 *                more width past it only pads cells — so it is the cap, and it is a fact about the
 *                content rather than a number somebody liked.
 *
 * FLUID, NOT FIXED. The cap is a ceiling, never a floor:
 *
 *     width: min(<cap>px, calc(100% - 2 * <gutter>))
 *
 * so on a 1280 laptop the viewport binds and the data screen takes 1229px (96%), and on a 2560
 * monitor the cap binds at 2044 and the page stops growing because the grid has nothing left to
 * show. The old 1240 could do neither.
 *
 * THE GUTTER SCALES WITH THE VIEWPORT. `clamp(24px, 2vw, 64px)` — see `GUTTER` for why 2vw and not
 * 3. A constant 24px sitting next to a 276px margin was the visible symptom of a layout that had
 * two unrelated ideas about edge space.
 *
 * WHAT THIS DOES NOT DO. It does not make tables fit. The owner is explicit — "I like having the
 * horizontal scroll bar when needed" — so a table that genuinely exceeds the page still scrolls,
 * INSIDE its own `overflow-x` container (`ConfirmGrid`, `CompetingListings`, `ResultScreen` all
 * have one), never by dragging the page sideways and taking the header and the controls with it.
 */
export type ContentWidth = "prose" | "form" | "data" | "full" | "bleed";

/**
 * The named measures, in pixels. `copy` is not a page measure — it is the paragraph measure a block
 * of explanatory text carries INSIDE a wider page, so that widening the page for a grid never
 * stretches a sentence to 150 characters. The app already used 640 for this in a dozen places; it is
 * named here so the next one does not have to re-pick it.
 */
export const measure = {
  copy: 640,
  prose: 880,
  form: 1200,
  data: 2044,
} as const;

/**
 * The page gutter, as its three parts, so the CSS below and the model in `gutterAt` cannot drift.
 *
 * 2vw, MEASURED RATHER THAN CHOSEN. The first cut of this was 3vw, and the Chromium sweep caught
 * what that costs: at 1280 it gave the comparable-sales grid 1187px where the old fixed 1240 box
 * gave it 1216. A width system that makes the owner's laptop WORSE to buy elegance on a monitor
 * they do not own is not a fix. At 2vw every one of 1280/1440/1512/1728/1920/2560 is at least as
 * wide as it was before, and the grid holds ~96% of the glass until its own cap takes over.
 * The 24px floor is the padding the app already used, so nothing about a phone changes; the 64px
 * ceiling is the one `landing.css` already uses for `--gutter`.
 */
/* LEFT AT 24 / 2 / 64  (re-checked 2026-08-30).
   A design review proposed retuning this to the prototype's `clamp(20px, 2.2vw, 44px)`. Worked
   through against the reasoning above, that is a regression: at 1280 — the owner's own laptop —
   2.2vw resolves to 28.16 and gives the comparable grid 1223.68px, against 1228.8 today. The note
   above exists precisely because an earlier 3vw cut did the same thing, and the rule it set is
   that a width system must not make his laptop worse to buy elegance on a monitor he does not own.
   The prototype can afford a tighter gutter because it has no page cap at all; now that prose and
   form are full bleed, this app gets the same result without touching the number that was
   measured. */
export const GUTTER = { minPx: 24, vw: 2, maxPx: 64 } as const;

export const PAGE_GUTTER = `clamp(${GUTTER.minPx}px, ${GUTTER.vw}vw, ${GUTTER.maxPx}px)`;

/**
 * THE PAGE CAP IS GONE. THE CAP BELONGS TO THE PARAGRAPH.  (2026-08-30)
 *
 * WHAT WAS WRONG, and it took two independent design reviews to see it. The reasoning above is a
 * correct statement about a PARAGRAPH — 880px at 16px Inter really is ~95 characters, and widening
 * a column of prose really does make it harder to read. It was then applied to a PAGE: a screen
 * that also holds a two-column field grid, a toolbar, a table and a status band.
 *
 * The measured consequence at 1512px: `/branding` and the report screen rendered 880px of content
 * with 316px of empty grey on each side — 58% of the glass used. At 1920 it is 46%. And because
 * `<header>` is full-bleed while `<main>` was a centred column, the chrome and the content
 * disagreed about where the page's edges are: on `/briefs` at 1920 the logo's left edge sat 308px
 * from the table's left edge, and "Sign out" ended 330px past the table's right edge. Nothing on
 * the screen lined up with anything else, which is most of why the product read as a column of
 * objects floating on a grey field rather than as an application.
 *
 * THE APPROVED PROTOTYPES CONTAIN NO PAGE-LEVEL `max-width` AT ALL. Checked, both files: every
 * `max-width` in either is a paragraph cap on one sentence — `.chead .sub { max-width: 64ch }`,
 * `.foot .n { max-width: 72ch }`. The frame is the viewport, inset by one gutter that every band
 * shares, which is exactly why every left edge in the prototype falls on one vertical line.
 *
 * So the page is full bleed and `measure.copy` (640) does the job it was always the right tool
 * for — it is now the ONLY cap on a sentence, instead of the inner of two. The named pixel
 * measures below survive for BLOCK-level use inside a page (a form panel, the comp grid's own
 * column sum) and `contentWidthAt` still models them for the tests.
 *
 * `ConfirmGrid`'s 2044 is a fact about its eighteen columns and still governs the grid itself: it
 * keeps its own `overflow-x` container, so the owner's "I like having the horizontal scroll bar
 * when needed" holds, and on wide glass it simply stops growing once it has shown everything.
 */
export const CONTENT_MAX_PX: Record<ContentWidth, number | null> = {
  /* FULL BLEED, FOR A SCREEN WHOSE CONTENT ACTUALLY FILLS (2026-08-30).
     The note below this one says the caps are load-bearing "until the wizard's content fills — the
     prototype's arrangement, two columns of real content — and then remove these. That is a layout
     change per screen, not a constant." Step 1 is now that layout: `.panelWrap` is the prototype's
     own `repeat(auto-fit, minmax(320px, 1fr))` grid of three panels, so there is nothing left for a
     cap to protect. It is a per-screen opt-in, not a new default — every screen still on a single
     column of prose keeps `prose` or `form`, because uncapping those moves empty margin to one side
     rather than filling anything. */
  full: null,
  /* EDGE TO EDGE, WITH NO GUTTER AT ALL — for a screen that supplies its own (2026-08-30).
     `full` is uncapped but still inset by one gutter, which is right for a page of panels. It is
     wrong for the CRM: the prototype's `.frame` puts the rail flush against the left edge of the
     glass and lets `.chead` and `.board` apply `var(--gutter)` themselves. Rendered at `full` the
     rail floats off the edge and every band inside it is inset twice.
     The only kind that may skip the shared gutter is one whose own children re-apply it. */
  bleed: null,
  /* RESTORED, AFTER TRYING THE OPPOSITE  (2026-08-30, same day).
     A design review observed — correctly — that neither approved prototype has a page-level
     max-width, and I removed these. Rendered, that was wrong, and the reason is instructive: the
     prototypes have no page cap BECAUSE THEIR PAGES FILL THE WIDTH. Their content is
     `repeat(auto-fit, minmax(215px, 1fr))` card strips, two-column grids and edge-to-edge metric
     bands. This app's wizard pages are a single column of prose whose blocks are capped at
     `measure.copy` (640) — so uncapping the PAGE did not fill anything, it just moved 316px of
     empty margin from both sides of step 1 to all of it on the right. Lopsided reads as broken;
     symmetric reads as deliberate.
     The right fix is to make the wizard's content fill — the prototype's `.grid2` arrangement,
     two columns of real content — and then remove these. That is a layout change per screen, not
     a constant, and it is the follow-up. Until then these caps are load-bearing.
     The CRM is unaffected either way: it renders at `content="data"`, whose 2044 cap does not bind
     below a 2100px viewport. */
  prose: measure.prose,
  form: measure.form,
  /* `data` KEEPS ITS CAP, and it is the one measure that always deserved one. 2044 is not a
     reading preference — it is the sum of `ConfirmGrid.COMP_COLUMNS`' eighteen declared widths,
     i.e. the point past which the grid has shown everything it has. The table is
     `table-layout: fixed` over a `<colgroup>` of those widths, so a container wider than their sum
     distributes the surplus INTO the columns: remove this cap and a 2560px monitor pads every cell
     instead of ending the table. Verified as the one place full bleed would regress. */
  data: measure.data,
};

/**
 * The width declaration itself. `100%` rather than `100vw` on purpose: `100vw` includes the
 * vertical scrollbar, so a page with one would be 15px wider than its own viewport and scroll
 * sideways — the exact defect this change is meant to remove.
 */
export function contentWidthCss(kind: ContentWidth): string {
  // `bleed` is the one kind with no gutter: its content supplies its own. See CONTENT_MAX_PX.
  if (kind === "bleed") return "100%";
  const cap = CONTENT_MAX_PX[kind];
  return cap === null
    ? `calc(100% - 2 * ${PAGE_GUTTER})`
    : `min(${cap}px, calc(100% - 2 * ${PAGE_GUTTER}))`;
}

/** The page box for a given content kind. Full bleed, inset by one shared gutter. */
export function pageMeasure(kind: ContentWidth): CSSProperties {
  return { width: contentWidthCss(kind), marginInline: "auto" };
}

/**
 * A band that must reach both edges of the glass from inside a gutter-padded page — the
 * prototype's full-bleed page head and its edge-to-edge metric strip. Applied to a child of the
 * page box, it cancels the gutter on both sides and re-applies it as padding, so the band's
 * BACKGROUND runs to the viewport edge while its CONTENT stays on the same vertical line as
 * everything else.
 */
export const bleed: CSSProperties = {
  marginInline: `calc(-1 * ${PAGE_GUTTER})`,
  paddingInline: PAGE_GUTTER,
};

/* ----------------------------------------------------------------------------------------- *
 * THE SAME RULE, AS ARITHMETIC. Not a duplicate of the CSS — the model the CSS is generated
 * from, so a test (and a Playwright sweep) can state what a given screen size is supposed to
 * get and fail if the browser disagrees.
 * ----------------------------------------------------------------------------------------- */

/** The gutter the browser will resolve `PAGE_GUTTER` to at this viewport width. */
export function gutterAt(viewportPx: number): number {
  return Math.min(GUTTER.maxPx, Math.max(GUTTER.minPx, (viewportPx * GUTTER.vw) / 100));
}

/** The content width a page of `kind` gets at this viewport width. A `null` cap is full bleed, so
 *  the viewport minus its two gutters is the whole answer. */
export function contentWidthAt(kind: ContentWidth, viewportPx: number): number {
  const bleedWidth = viewportPx - 2 * gutterAt(viewportPx);
  // `bleed` takes the glass whole — no gutter subtracted, because its own bands apply one.
  if (kind === "bleed") return viewportPx;
  const cap = CONTENT_MAX_PX[kind];
  return cap === null ? bleedWidth : Math.min(cap, bleedWidth);
}
